/**
 * Axios instance of the admin app (SPEC-FRONTEND §2 «http.ts», admin variant — no onboarding).
 *
 * 1. `Authorization` is never attached to public endpoints: `/admin/auth/`, `/auth/refresh/`,
 *    `/auth/logout/`, `/geo/`, `/meta/`, `/public/`.
 * 2. `Accept-Language` follows the current UI language.
 * 3. On 401 the access token is refreshed under the Web Lock `admin-refresh`. Tokens are read from
 *    localStorage inside the lock: if the access token there differs from the one the request
 *    failed with, another request/tab already refreshed and the request is simply replayed.
 *    The session ends only when the refresh itself is rejected inside the lock.
 * 4. Cross-tab sign-in/sign-out sync (`storage` event) lives in `AuthProvider`.
 * 5. 403 `user_blocked` ends the session with reason `blocked`; `AuthProvider` shows
 *    `errors:user_blocked` and the guard redirects to `/login?blocked=1`.
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { endSession, tokenStorage } from '@/auth/session';
import { currentLanguage } from '@/i18n';

import { apiErrorCode } from './errors';
import type { TokenPair } from './types';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    /** Set once the request has been replayed after a token refresh (prevents refresh loops). */
    _authRetried?: boolean;
  }
}

export const API_BASE_URL = '/api';

/** Web Lock name shared by all admin tabs (`<app>-refresh`). */
const REFRESH_LOCK_NAME = 'admin-refresh';

const PUBLIC_PREFIXES = [
  '/admin/auth/',
  '/auth/refresh/',
  '/auth/logout/',
  '/geo/',
  '/meta/',
  '/public/',
] as const;

/** Path relative to `/api` for relative (`/admin/me/`) and absolute (`http://…/api/…`) URLs. */
function apiPath(url: string | undefined): string {
  if (!url) return '/';
  let path = url;
  if (/^https?:\/\//i.test(url)) {
    try {
      path = new URL(url).pathname;
    } catch {
      return url;
    }
  }
  path = path.split(/[?#]/, 1)[0];
  if (!path.startsWith('/')) path = `/${path}`;
  return path.startsWith(`${API_BASE_URL}/`) ? path.slice(API_BASE_URL.length) : path;
}

/** Whether a request must go without the admin bearer token (rule 1). */
export function isPublicEndpoint(url: string | undefined): boolean {
  const path = apiPath(url);
  return PUBLIC_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export const http = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
  // DRF reads repeated keys (`?status=a&status=b`), not the `status[]=` bracket syntax.
  paramsSerializer: { indexes: null },
});

/** Interceptor-free client for the refresh call, so a failing refresh can never recurse. */
const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15_000,
  headers: { Accept: 'application/json' },
});

function bearerToken(config: InternalAxiosRequestConfig): string | null {
  const header = config.headers.get('Authorization');
  return typeof header === 'string' && header.startsWith('Bearer ') ? header.slice(7) : null;
}

/**
 * Blob requests (KYC images) receive their JSON error bodies as Blobs; decode them so that the
 * `user_blocked` check below and `parseApiError` work the same for every response type.
 */
async function decodeBlobErrorBody(error: AxiosError): Promise<void> {
  const response = error.response;
  if (!response || typeof Blob === 'undefined' || !(response.data instanceof Blob)) return;
  if (!response.data.type.includes('json')) return;
  try {
    response.data = JSON.parse(await response.data.text()) as unknown;
  } catch {
    // Not valid JSON after all: keep the Blob.
  }
}

let localLockTail: Promise<unknown> = Promise.resolve();

/** Runs `task` exclusively across tabs (Web Locks), or at least within this tab as a fallback. */
async function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return await navigator.locks.request(REFRESH_LOCK_NAME, task);
  }
  const run = localLockTail.then(task, task);
  localLockTail = run.catch(() => undefined);
  return await run;
}

/**
 * Returns a usable access token after a 401, or `null` when the session is over.
 * Network/5xx failures of the refresh call are re-thrown and keep the session intact.
 */
function refreshAccessToken(failedToken: string | null): Promise<string | null> {
  return withRefreshLock(async () => {
    const current = tokenStorage.getAccess();
    if (current && current !== failedToken) return current;

    const refresh = tokenStorage.getRefresh();
    if (!refresh) {
      endSession('expired');
      return null;
    }

    try {
      const { data } = await refreshClient.post<TokenPair>(
        '/auth/refresh/',
        { refresh },
        { headers: { 'Accept-Language': currentLanguage() } },
      );
      tokenStorage.set(data);
      return data.access;
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 400 || status === 401 || status === 403) {
        // The server rejected the refresh token: expired (admin sessions last ≤ 12 h),
        // blacklisted after rotation/logout, or the account was blocked.
        const blocked =
          status === 403 && axios.isAxiosError(error) && apiErrorCode(error.response?.data) === 'user_blocked';
        endSession(blocked ? 'blocked' : 'expired');
        return null;
      }
      throw error;
    }
  });
}

http.interceptors.request.use((config) => {
  config.headers.set('Accept-Language', currentLanguage());
  if (!isPublicEndpoint(config.url)) {
    const access = tokenStorage.getAccess();
    if (access) config.headers.set('Authorization', `Bearer ${access}`);
    else config.headers.delete('Authorization');
  }
  return config;
});

http.interceptors.response.use(undefined, async (error: unknown) => {
  if (!axios.isAxiosError(error) || !error.config || !error.response) throw error;
  await decodeBlobErrorBody(error);

  const { config, response } = error;
  // Public endpoints (sign-in, refresh, logout…) report their errors to the caller as is.
  if (isPublicEndpoint(config.url)) throw error;

  if (response.status === 401 && !config._authRetried) {
    const freshToken = await refreshAccessToken(bearerToken(config));
    if (freshToken === null) throw error;
    config._authRetried = true;
    return http.request(config);
  }

  if (response.status === 403 && apiErrorCode(response.data) === 'user_blocked') {
    endSession('blocked');
  }
  throw error;
});
